import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import heic2any from "heic2any";
import { useApiQuery } from "../../../../../infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../infrastructure/routes/endpointsMetadata";
import axiosInstance, {
  setAxiosToken,
} from "../../../../../infrastructure/helpers/axiosHelper";
import { useAuthentication } from "../../../../../infrastructure/hooks/useAuthentication";
import { getErrorToast } from "../../../../../infrastructure/utilities/toastUtility";
import { IPortfolioImage } from "../models/portfolioImage";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB — must match backend
const MAX_IMAGES_PER_PAIR = 20; // must match backend

/**
 * Manages the portfolio gallery for a single (service, employee) pair: loads
 * existing images, uploads new ones (with HEIC conversion), and deletes them.
 */
export const useEmployeePortfolio = (
  serviceTypeId: number | undefined,
  employeeId: number
) => {
  const { currentUser } = useAuthentication();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const enabled = !!serviceTypeId && serviceTypeId > 0;

  const queryKey = [
    endpointsMetadata.serviceEmployeePortfolio.getPortfolio,
    serviceTypeId,
    employeeId,
  ];

  const { data: images, isLoading } = useApiQuery<IPortfolioImage[]>({
    queryKey,
    endpoint: `${endpointsMetadata.serviceEmployeePortfolio.getPortfolio}?serviceTypeId=${serviceTypeId}&employeeId=${employeeId}`,
    enabled,
    globalIsLoading: false,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, serviceTypeId, employeeId]);

  // Uploads a single file. Returns true on success. Validation and the input
  // reset are handled by the batch caller so several files can be uploaded at once.
  const uploadSingle = useCallback(
    async (file: File): Promise<boolean> => {
      setAxiosToken(currentUser?.authToken);

      let uploadFileToSend: File = file;
      const isHeic =
        file.type === "image/heic" ||
        file.type === "image/heif" ||
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif");

      if (isHeic) {
        try {
          const converted = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.9,
          });
          const blob = Array.isArray(converted) ? converted[0] : converted;
          const jpegName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
          uploadFileToSend = new File([blob], jpegName, { type: "image/jpeg" });
        } catch {
          // Conversion failed — send original; the server validates the type.
        }
      }

      const formData = new FormData();
      formData.append("serviceTypeId", String(serviceTypeId));
      formData.append("employeeId", String(employeeId));
      formData.append("file", uploadFileToSend);

      await axiosInstance.post(
        endpointsMetadata.serviceEmployeePortfolio.upload,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      return true;
    },
    [currentUser, serviceTypeId, employeeId]
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      // Copy out of the live FileList before any async work; resetting the input
      // value later would otherwise empty it mid-loop.
      const selected = Array.from(input.files ?? []);
      if (!enabled || selected.length === 0) {
        input.value = "";
        return;
      }

      const remainingSlots = MAX_IMAGES_PER_PAIR - (images?.length ?? 0);
      if (remainingSlots <= 0) {
        getErrorToast(
          `Достигнат е максималният брой снимки (${MAX_IMAGES_PER_PAIR}) за този служител.`
        );
        input.value = "";
        return;
      }

      const toUpload = selected.slice(0, remainingSlots);
      if (selected.length > remainingSlots) {
        getErrorToast(
          `Можете да добавите още само ${remainingSlots} снимки за този служител.`
        );
      }

      setIsUploading(true);
      let uploadedAny = false;
      try {
        // Upload sequentially so SortOrder stays in the chosen order.
        for (const file of toUpload) {
          if (file.size > MAX_FILE_SIZE_BYTES) {
            getErrorToast(
              `„${file.name}" надвишава максималния размер от 5 MB и беше пропусната.`
            );
            continue;
          }

          try {
            // eslint-disable-next-line no-await-in-loop
            await uploadSingle(file);
            uploadedAny = true;
          } catch {
            getErrorToast(`Качването на „${file.name}" беше неуспешно.`);
          }
        }
      } finally {
        if (uploadedAny) {
          refresh();
        }
        setIsUploading(false);
        input.value = "";
      }
    },
    [enabled, images, uploadSingle, refresh]
  );

  const removeImage = useCallback(
    async (imageId: number) => {
      try {
        setAxiosToken(currentUser?.authToken);
        await axiosInstance.delete(
          `${endpointsMetadata.serviceEmployeePortfolio.delete}?imageId=${imageId}`
        );
        refresh();
      } catch {
        getErrorToast("Изтриването на снимката беше неуспешно.");
      }
    },
    [currentUser, refresh]
  );

  return {
    images: images ?? [],
    isLoading,
    isUploading,
    fileInputRef,
    handleFileChange,
    removeImage,
    canUploadMore: (images?.length ?? 0) < MAX_IMAGES_PER_PAIR,
  };
};
